import ky from 'ky';
import Config from './config';
import type { CreditsData } from './credits/lib/CreditsUtils';
import type { D3ForumData } from './d3forum/lib/D3ForumUtils';
import type { IndexesData } from './database/lib/IndexUtil';
import type { ItemCore, SimPFLink } from './database/lib/ItemUtil';
import type { PicoConfigData } from './pico/lib/PicoUtils';

export interface RankedItem extends ItemCore {
    title: string;
    count: number;
}

export interface RankedContributor {
    uid: number;
    uname: string;
    name: string;
    count: number;
}

export interface RankedKeyword {
    keyword: string;
    count: number;
}

export interface RankingsData {
    accessed: RankedItem[];
    downloaded: RankedItem[];
    contributed: RankedContributor[];
    searched: RankedKeyword[];
}

export interface RecentContentItem extends ItemCore {
    title: string;
    last_update_date: number;
}

/**
 * Data converted from the original XOOPS modules. It is served with the rest of
 * static-contents/ rather than bundled, so it is fetched once at start up and
 * handed to the singletons before the app renders.
 */
export interface SiteData {
    tree: IndexesData;
    simpfLinks: SimPFLink[];
    rankings: RankingsData;
    recentContents: RecentContentItem[];
    credits: CreditsData;
    pico: PicoConfigData[];
    d3forum: D3ForumData[];
}

// ky hands the URL to fetch()/Request, which rejects bare relative paths.
const resolve = (path: string): string => new URL(Config.BASE_URL + path, window.location.href).toString();

export const loadSiteData = async (): Promise<SiteData> => {
    const [tree, simpfLinks, rankings, recentContents, credits, pico, d3forum] = await Promise.all([
        ky.get(resolve('/modules/xoonips/tree.json')).json<IndexesData>(),
        ky.get(resolve('/modules/xoonips/simpf-links.json')).json<SimPFLink[]>(),
        ky.get(resolve('/modules/xoonips/rankings.json')).json<RankingsData>(),
        ky.get(resolve('/modules/xoonips/recent-contents.json')).json<RecentContentItem[]>(),
        ky.get(resolve('/modules/credits/credits.json')).json<CreditsData>(),
        // Each pico module keeps its own index next to its pages.
        Promise.all(
            Config.PICO_MODULES.map((name) => ky.get(resolve(`/modules/${name}/config.json`)).json<PicoConfigData>())
        ),
        // Likewise for each d3forum module.
        Promise.all(
            Config.D3FORUM_MODULES.map((name) => ky.get(resolve(`/modules/${name}/d3forum.json`)).json<D3ForumData>())
        ),
    ]);
    return { tree, simpfLinks, rankings, recentContents, credits, pico, d3forum };
};

let loaded: SiteData | null = null;

export const setSiteData = (data: SiteData): void => {
    loaded = data;
};

export const siteData = (): SiteData => {
    if (loaded === null) {
        throw new Error('site data was read before loadSiteData() completed');
    }
    return loaded;
};
